import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '@/context/AuthContext'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { adminApi } from '@/utils/api'
import type { AdminOrganizationStat } from '@/types/admin'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PhoneNumberInput } from '@/components/PhoneNumberInput'

export default function AdminOrganizationsPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const userName = getUserDisplayName(user)
  const userInitials = getUserInitials(user)
  const [pending, setPending] = useState<AdminOrganizationStat[]>([])
  const [stats, setStats] = useState<AdminOrganizationStat[]>([])
  const [error, setError] = useState<string | null>(null)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteLoading, setInviteLoading] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [reactivatingId, setReactivatingId] = useState<string | null>(null)
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
  })

  useEffect(() => {
    const load = async () => {
      try {
        setError(null)
        const [pendingRes, statsRes] = await Promise.all([
          adminApi.organizationsPending(),
          adminApi.organizationStats(),
        ])
        const pendingData =
          (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
          (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
          []
        const statsData =
          (statsRes as unknown as { data?: AdminOrganizationStat[] }).data ||
          (statsRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
          []
        setPending(Array.isArray(pendingData) ? pendingData : [])
        setStats(Array.isArray(statsData) ? statsData : [])
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load organizations')
      }
    }
    void load()
  }, [])

  const totals = useMemo(() => {
    const totalOrgs = stats.length
    const pendingCount = pending.length
    const suspendedCount = stats.filter((s) => s.status === 'suspended').length
    const avgCompletion =
      stats.length > 0
        ? Math.round(
            stats.reduce((sum, item) => sum + (item.completionRate ?? 0), 0) / stats.length
          )
        : 0

    return {
      totalOrgs,
      pendingCount,
      suspendedCount,
      avgCompletion,
    }
  }, [stats, pending])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    setInviteError(null)
    setInviteSuccess(null)
    setInviteLoading(true)
    try {
      await adminApi.inviteOrganization({
        name: inviteForm.name,
        email: inviteForm.email,
        phoneNumber: inviteForm.phoneNumber,
      })
      setInviteSuccess('Invitation sent successfully.')
      setInviteForm({
        name: '',
        email: '',
        phoneNumber: '',
      })
      setInviteOpen(false)
      // refresh data
      const pendingRes = await adminApi.organizationsPending()
      const pendingData =
        (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
        (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
        []
      setPending(Array.isArray(pendingData) ? pendingData : [])
    } catch (err) {
      setInviteError((err as Error)?.message || 'Failed to invite organization')
    } finally {
      setInviteLoading(false)
    }
  }

  const refreshData = async () => {
    const [pendingRes, statsRes] = await Promise.all([
      adminApi.organizationsPending(),
      adminApi.organizationStats(),
    ])
    const pendingData =
      (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
      (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
      []
    const statsData =
      (statsRes as unknown as { data?: AdminOrganizationStat[] }).data ||
      (statsRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
      []
    setPending(Array.isArray(pendingData) ? pendingData : [])
    setStats(Array.isArray(statsData) ? statsData : [])
  }

  const handleReactivate = async (organizationId: string) => {
    if (reactivatingId) return
    setReactivatingId(organizationId)
    setError(null)
    try {
      await adminApi.reactivateOrganization(organizationId)
      setInviteSuccess('Organization reactivated successfully!')
      setTimeout(() => setInviteSuccess(null), 3000)
      await refreshData()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to reactivate organization')
    } finally {
      setReactivatingId(null)
    }
  }

  const handleResendInvite = async (organizationId: string) => {
    if (resendingId) return

    setResendingId(organizationId)
    setError(null)

    try {
      await adminApi.resendOrganizationInvite(organizationId)

      // Show success message briefly
      setInviteSuccess('Invitation resent successfully!')
      setTimeout(() => setInviteSuccess(null), 3000)

      // Refresh pending list
      const pendingRes = await adminApi.organizationsPending()
      const pendingData =
        (pendingRes as unknown as { data?: AdminOrganizationStat[] }).data ||
        (pendingRes as unknown as { data?: { data?: AdminOrganizationStat[] } }).data?.data ||
        []
      setPending(Array.isArray(pendingData) ? pendingData : [])
    } catch (err) {
      setError((err as Error)?.message || 'Failed to resend invitation')
    } finally {
      setResendingId(null)
    }
  }

  return (
    <div className='min-h-screen bg-[#edeff3]'>
      <div className='p-4 md:p-6 lg:p-8 space-y-6'>
        {/* Header */}
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8'>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Organizations</h1>
              <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>
                Manage invites, approvals, suspensions, and organization performance.
              </p>
            </div>
            <div className='flex items-center gap-3'>
              <div className='h-[47px] w-[48px] rounded-full bg-[#434343] flex items-center justify-center text-white font-bold text-sm'>
                {userInitials}
              </div>
              <span className='text-[24px] font-bold text-[#353535] hidden sm:inline'>{userName}</span>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4'>
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 cursor-pointer hover:bg-[rgba(237,239,243,0.6)] transition-colors'>
            <p className='text-[13px] text-[#4e4d4d]'>Total Orgs</p>
            <p className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>{(totals.totalOrgs || 0).toLocaleString()}</p>
            <p className='text-[13px] text-[#4e4d4d]'>All organizations</p>
          </div>
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 cursor-pointer hover:bg-[rgba(237,239,243,0.6)] transition-colors'>
            <p className='text-[13px] text-[#4e4d4d]'>Pending</p>
            <p className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>{(totals.pendingCount || 0).toLocaleString()}</p>
            <p className='text-[13px] text-[#4e4d4d]'>Awaiting approval</p>
          </div>
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 cursor-pointer hover:bg-[rgba(237,239,243,0.6)] transition-colors'>
            <p className='text-[13px] text-[#4e4d4d]'>Suspended</p>
            <p className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>{(totals.suspendedCount || 0).toLocaleString()}</p>
            <p className='text-[13px] text-[#4e4d4d]'>Suspended</p>
          </div>
          <div className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 cursor-pointer hover:bg-[rgba(237,239,243,0.6)] transition-colors'>
            <p className='text-[13px] text-[#4e4d4d]'>Avg Completion</p>
            <p className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>{totals.avgCompletion}%</p>
            <p className='text-[13px] text-[#4e4d4d]'>Completion rates</p>
          </div>
        </div>

        {/* Stats by Organization */}
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-4'>
          <h3 className='text-[18px] font-bold text-[#151515]'>Stats by Organization</h3>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
            {stats.length === 0 && (
              <p className='text-[14px] text-[#4e4d4d] col-span-3'>No organization stats available.</p>
            )}
            {stats.map((item) => {
              const name = item.name || 'Organization'
              const completion = item.completionRate ?? 0
              const couples = item.couples ?? 0
              const isSuspended = item.status === 'suspended'
              return (
                <div
                  key={item.id}
                  className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 cursor-pointer hover:bg-[rgba(237,239,243,0.6)] transition-colors'
                  onClick={() => navigate(`/admin/organizations/${item.id}`)}
                >
                  <div className='flex items-start justify-between gap-2'>
                    <p className='text-[13px] text-[#4e4d4d]'>{name}</p>
                    {isSuspended && (
                      <span className='inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-800 shrink-0'>
                        Suspended
                      </span>
                    )}
                  </div>
                  <p className='text-[18px] font-bold text-[#151515]'>Completion {completion}%</p>
                  <p className='text-[13px] text-[#4e4d4d]'>Couples: {couples}</p>
                  <div className='flex items-center justify-between mt-1'>
                    <p className='text-[13px] text-[#FF6600] font-medium'>View details &rarr;</p>
                    {isSuspended && (
                      <button
                        type='button'
                        onClick={(e) => { e.stopPropagation(); handleReactivate(item.id) }}
                        disabled={reactivatingId === item.id}
                        className='px-2.5 py-1 rounded-[6px] bg-green-50 text-green-700 hover:bg-green-100 text-[12px] font-medium disabled:opacity-50'
                      >
                        {reactivatingId === item.id ? 'Reactivating...' : 'Reactivate'}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          <p className='text-[13px] text-[#4e4d4d]'>Surface completion rates and totals for comparisons.</p>
        </div>

        {/* Pending Approvals */}
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-4'>
          <div className='flex items-center justify-between'>
            <h3 className='text-[18px] font-bold text-[#151515]'>Pending Approvals</h3>
            <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
              <SheetTrigger asChild>
                <button className='px-4 py-2 bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold rounded-[8px] text-[14px]'>
                  Invite Organization
                </button>
              </SheetTrigger>
              <SheetContent side='right' className='w-full sm:max-w-lg overflow-y-auto'>
                <SheetHeader>
                  <SheetTitle>Invite Organization</SheetTitle>
                </SheetHeader>
                <form className='mt-6 space-y-3' onSubmit={handleInvite}>
                  {inviteError && (
                    <div className='text-[14px] text-red-600 bg-red-50 border border-red-200 rounded-[8px] px-3 py-2'>
                      {inviteError}
                    </div>
                  )}
                  {inviteSuccess && (
                    <div className='text-[14px] text-green-700 bg-green-50 border border-green-200 rounded-[8px] px-3 py-2'>
                      {inviteSuccess}
                    </div>
                  )}
                  <div className='space-y-1'>
                    <label className='text-[13px] font-semibold text-[#151515] text-start block'>Organization Name</label>
                    <input
                      type='text'
                      placeholder='e.g. The Redeemed Christian Church of God'
                      className='w-full rounded-[8px] border border-[#E5E7EB] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                      required
                      value={inviteForm.name}
                      onChange={(e) => setInviteForm((prev) => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-[13px] font-semibold text-[#151515] text-start block'>Organization Email</label>
                    <input
                      type='email'
                      placeholder='e.g. info@organization.org'
                      className='w-full rounded-[8px] border border-[#E5E7EB] px-3 py-2 text-[14px] outline-none focus:ring-1 focus:ring-[#FF6600]'
                      required
                      value={inviteForm.email}
                      onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className='space-y-1'>
                    <label className='text-[13px] font-semibold text-[#151515] text-start block'>Organization Phone</label>
                    <PhoneNumberInput
                      value={inviteForm.phoneNumber}
                      onChange={(value) => setInviteForm((prev) => ({ ...prev, phoneNumber: value }))}
                      placeholder='Phone number'
                      required
                      containerClassName='border-[#E5E7EB]'
                      inputClassName='py-0'
                    />
                  </div>
                  <div className='flex justify-end gap-2 pt-2'>
                    <Button type='button' variant='outline' onClick={() => setInviteOpen(false)} disabled={inviteLoading}>
                      Cancel
                    </Button>
                    <Button type='submit' className='bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold' disabled={inviteLoading}>
                      {inviteLoading ? 'Sending...' : 'Send Invite'}
                    </Button>
                  </div>
                </form>
              </SheetContent>
            </Sheet>
          </div>

          {/* Table */}
          <div className='overflow-x-auto'>
            {pending.length === 0 ? (
              <p className='py-3 text-[14px] text-[#4e4d4d] text-center'>No pending organizations.</p>
            ) : (
              <table className='w-full'>
                <thead>
                  <tr className='border-b border-[#eaecf0] text-start'>
                    <th className='text-left py-3 px-4 text-[13px] font-semibold text-[#4e4d4d] uppercase tracking-wider'>
                      Organization Name
                    </th>
                    <th className='text-left py-3 px-4 text-[13px] font-semibold text-[#4e4d4d] uppercase tracking-wider'>
                      ID / Reference
                    </th>
                    <th className='text-center py-3 px-4 text-[13px] font-semibold text-[#4e4d4d] uppercase tracking-wider'>
                      Status
                    </th>
                    <th className='text-center py-3 px-4 text-[13px] font-semibold text-[#4e4d4d] uppercase tracking-wider'>
                      Couples
                    </th>
                    <th className='text-center py-3 px-4 text-[13px] font-semibold text-[#4e4d4d] uppercase tracking-wider'>
                      Completion Rate
                    </th>
                    <th className='text-right py-3 px-4 text-[13px] font-semibold text-[#4e4d4d] uppercase tracking-wider'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((item) => {
                    const name = item.name || 'Organization'
                    const adminEmail = item.id || ''
                    const status = item.status || 'pending'
                    const couples = item.couples ?? 0
                    const completionRate = item.completionRate ?? 0

                    return (
                      <tr key={item.id} className='border-b border-[#eaecf0] hover:bg-[#f9fafb] text-start'>
                        <td className='py-3 px-4'>
                          <p className='font-semibold text-[#151515] text-[14px]'>{name}</p>
                        </td>
                        <td className='py-3 px-4'>
                          <p className='text-[#4e4d4d] text-[13px] font-mono'>{adminEmail}</p>
                        </td>
                        <td className='py-3 px-4 text-center'>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[13px] font-medium ${
                            status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : status === 'suspended'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                        </td>
                        <td className='py-3 px-4 text-center'>
                          <p className='text-[#151515] text-[14px] font-medium'>{couples}</p>
                        </td>
                        <td className='py-3 px-4 text-center'>
                          <div className='flex items-center justify-center gap-2'>
                            <div className='w-16 bg-[rgba(237,239,243,0.44)] rounded-full h-2'>
                              <div
                                className='bg-[#FF6600] h-2 rounded-full'
                                style={{ width: `${completionRate}%` }}
                              />
                            </div>
                            <span className='text-[#151515] text-[14px] font-medium'>{completionRate}%</span>
                          </div>
                        </td>
                        <td className='py-3 px-4 text-right'>
                          <div className='flex justify-end gap-2'>
                            <button
                              onClick={() => navigate(`/admin/organizations/${item.id}`)}
                              className='px-3 py-1 rounded-[8px] bg-[rgba(237,239,243,0.44)] text-[#151515] hover:bg-[rgba(237,239,243,0.6)] text-[13px] font-medium'
                            >
                              View
                            </button>
                            {status === 'pending' && (
                              <button
                                onClick={() => handleResendInvite(item.id)}
                                disabled={resendingId === item.id}
                                className='px-3 py-1 rounded-[8px] bg-orange-50 text-[#FF6600] hover:bg-orange-100 text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                {resendingId === item.id ? 'Resending...' : 'Resend Invite'}
                              </button>
                            )}
                            {status === 'suspended' && (
                              <button
                                onClick={() => handleReactivate(item.id)}
                                disabled={reactivatingId === item.id}
                                className='px-3 py-1 rounded-[8px] bg-green-50 text-green-700 hover:bg-green-100 text-[13px] font-medium disabled:opacity-50 disabled:cursor-not-allowed'
                              >
                                {reactivatingId === item.id ? 'Reactivating...' : 'Reactivate'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {error && <p className='text-[14px] text-red-600'>{error}</p>}
        {inviteSuccess && !inviteOpen && (
          <p className='text-[14px] text-green-700'>{inviteSuccess}</p>
        )}
      </div>
    </div>
  )
}
